import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import courseService from '../../services/courseService';
import {
    FaUser,
    FaCrown,
    FaBook,
    FaChartBar,
    FaArrowRight,
    FaLock,
    FaClock,
    FaExclamationTriangle,
    FaGift,
    FaCreditCard,
    FaClipboard,
    FaQuestionCircle,
    FaStar,
    FaCheck
} from 'react-icons/fa';

import './style.css';

// Level codes in order - moved outside component for stability
const LEVEL_CODES = ['A0', 'A1.1', 'A1.2', 'A2.1', 'A2.2', 'B1.1', 'B1.2'];

const DashboardPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const {
        subscription,
        loading: subscriptionLoading,
        hasActiveSubscription,
        isDemoSubscription,
        isPaidSubscription,
        isExpired,
        daysRemaining
    } = useSubscription();

    const [timeRemaining, setTimeRemaining] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });

    // State for levels data
    const [levels, setLevels] = useState([]);
    const [levelsLoading, setLevelsLoading] = useState(true);
    const [levelsError, setLevelsError] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: { pathname: '/dashboard' } } });
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        // Debug logging
        console.log('Dashboard subscription check:', {
            subscriptionLoading,
            isExpired,
            hasActiveSubscription,
            subscription,
            subscriptionStatus: subscription?.status
        });

        // Redirect if subscription is expired or user has no active subscription
        if (!subscriptionLoading && subscription !== null) {
            if (isExpired || (!hasActiveSubscription && subscription?.status === 'expired')) {
                console.log('Redirecting to demo-expired page');
                navigate('/demo-expired');
            }
        }
    }, [isExpired, hasActiveSubscription, subscriptionLoading, navigate, subscription]);

    useEffect(() => {
        if (!isDemoSubscription || !subscription?.end_date) {
            return;
        }

        const calculateTimeRemaining = () => {
            const endDate = new Date(subscription.end_date);
            const now = new Date();
            const difference = endDate - now;

            if (difference <= 0) {
                setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            setTimeRemaining({ days, hours, minutes, seconds });
        };

        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);

        return () => clearInterval(interval);
    }, [isDemoSubscription, subscription]);

    // Fetch levels data
    useEffect(() => {
        const fetchLevels = async () => {
            try {
                setLevelsLoading(true);
                setLevelsError(null);

                // Fetch all levels from the API
                const allLevels = await courseService.getAllCourses();

                // Manual reordering to ensure proper sequence (A0 first, then A1.1, A1.2, etc.)
                const sortedLevels = [];

                // Make sure allLevels is an array before processing
                const safeAllLevels = Array.isArray(allLevels) ? allLevels : [];

                // Add levels in the exact order we want
                LEVEL_CODES.forEach(levelCode => {
                    const level = safeAllLevels.find(l => (l.level || '').trim() === levelCode);
                    if (level) {
                        sortedLevels.push(level);
                    }
                });

                // Add any remaining levels that don't match our predefined codes
                safeAllLevels.forEach(level => {
                    const levelCode = (level.level || '').trim();
                    if (!LEVEL_CODES.includes(levelCode) && !sortedLevels.includes(level)) {
                        sortedLevels.push(level);
                    }
                });

                setLevels(sortedLevels);
            } catch (err) {
                console.error('Error fetching levels:', err);
                setLevelsError(t('dashboardPage.failedLoadLevels'));
            } finally {
                setLevelsLoading(false);
            }
        };

        fetchLevels();
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!isAuthenticated) {
        return null;
    }

    // Render level cards based on fetched data
    const renderLevelCards = () => {
        if (levelsLoading) {
            return (
                <div className="courses-browse-grid">
                    {[...Array(7)].map((_, index) => (
                        <div key={index} className="course-level-card">
                            <div className="course-level-header" style={{ background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)' }}>
                                <h3>{t('dashboardPage.loading')}</h3>
                                <span className="course-level-tag">...</span>
                            </div>
                            <div className="course-level-body">
                                <div className="course-action-btn disabled">
                                    {t('dashboardPage.loading')} <FaLock />
                                </div>
                            </div>
                        </div>
                    ))}
                    <Link to="/levels" className="course-level-card view-all-card">
                        <div className="course-level-header" style={{ background: 'linear-gradient(135deg, #ffb100 0%, #ff9f00 100%)' }}>
                            <h3>{t('dashboardPage.allLevels')}</h3>
                            <span className="course-level-tag"><FaArrowRight /></span>
                        </div>
                        <div className="course-level-body">
                            <div className="course-action-btn view-all-btn">
                                {t('dashboardPage.viewDetails')} <FaArrowRight />
                            </div>
                        </div>
                    </Link>
                </div>
            );
        }

        if (levelsError) {
            return (
                <div className="courses-browse-grid">
                    <div className="error-message">
                        <p>{levelsError}</p>
                    </div>
                    <Link to="/levels" className="course-level-card view-all-card">
                        <div className="course-level-header" style={{ background: 'linear-gradient(135deg, #ffb100 0%, #ff9f00 100%)' }}>
                            <h3>{t('dashboardPage.allLevels')}</h3>
                            <span className="course-level-tag"><FaArrowRight /></span>
                        </div>
                        <div className="course-level-body">
                            <div className="course-action-btn view-all-btn">
                                {t('dashboardPage.viewDetails')} <FaArrowRight />
                            </div>
                        </div>
                    </Link>
                </div>
            );
        }

        // Function to get level color from API data or use default
        const getLevelColorFromData = (level) => {
            // If the API provides a color, use it; otherwise use a default
            if (level.color) {
                return level.color;
            }

            // Default color mapping based on level code
            const defaultColors = {
                'A0': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                'A1': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                'A2': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                'A3': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                'B1': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                'B2': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                'B3': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            };

            return defaultColors[level.level] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        };

        return (
            <div className="courses-browse-grid">
                {levels.map((level) => {
                    const levelColor = getLevelColorFromData(level);

                    // Determine button text based on subscription status
                    let buttonText = t('dashboardPage.startLearning');
                    let buttonPath = `/levels/${level.level}/lessons`;

                    if (!hasActiveSubscription) {
                        // No subscription - redirect to demo activation
                        buttonText = t('dashboardPage.accessDemo');
                        buttonPath = "/demo-confirmation";
                    } else if (isDemoSubscription) {
                        // Demo subscription - show demo access
                        buttonText = t('dashboardPage.accessDemo');
                        buttonPath = `/levels/${level.level}/lessons`;
                    } else if (isPaidSubscription) {
                        // Premium subscription - show start learning
                        buttonText = t('dashboardPage.startLearning');
                        buttonPath = `/levels/${level.level}/lessons`;
                    }

                    return (
                        <div key={level.id || level.level} className="course-level-card">
                            <div className="course-level-header" style={{ background: levelColor }}>
                                <h3>{(level.slug || level.title || level.level).toUpperCase()}</h3>
                                <span className="course-level-tag">{level.level}</span>
                            </div>
                            <div className="course-level-body">
                                <Link
                                    to={buttonPath}
                                    className="course-action-btn"
                                >
                                    {buttonText} {!hasActiveSubscription ? <FaLock /> : <FaArrowRight />}
                                </Link>
                            </div>
                        </div>
                    );
                })}
                {/* 8th Card - View All Levels */}
                <Link to="/levels" className="course-level-card view-all-card">
                    <div className="course-level-header" style={{ background: 'linear-gradient(135deg, #ffb100 0%, #ff9f00 100%)' }}>
                        <h3>ALL LEVELS</h3>
                        <span className="course-level-tag"><FaArrowRight /></span>
                    </div>
                    <div className="course-level-body">
                        <div className="course-action-btn view-all-btn">
                            View Details <FaArrowRight />
                        </div>
                    </div>
                </Link>
            </div>
        );
    };

    return (
        <div className="dashboard-page">
            <InternalNavbar />

            <div className="dashboard-container">
                <div className="dashboard-wrapper">
                    {/* Hero Section */}
                    <div className="dashboard-hero">
                        <div className="hero-content">
                            <div className="hero-greeting">
                                <h1>{t('dashboardPage.welcome')}, {user?.first_name}!</h1>
                                <p>{t('dashboardPage.continueJourney')}</p>
                            </div>
                            {user?.profile_picture ? (
                                <img
                                    src={user.profile_picture}
                                    alt={user.first_name}
                                    className="hero-avatar"
                                />
                            ) : (
                                <div className="hero-avatar-placeholder">
                                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subscription Alert - Moved to be first section under dashboard-hero */}
                    {!hasActiveSubscription && !subscriptionLoading && (
                        <div className="alert-card inactive-alert">
                            <div className="alert-header">
                                <div className="alert-icon">
                                    <FaLock />
                                </div>
                                <div className="alert-title">
                                    <h3>{t('dashboardPage.noActiveSubscription')}</h3>
                                    <p>{t('dashboardPage.getStarted')}</p>
                                </div>
                            </div>
                            <div className="alert-actions">
                                <Link to="/subscribe" className="alert-btn primary">
                                    {t('dashboardPage.enrollNow')} <FaArrowRight />
                                </Link>
                                <Link to="/demo-confirmation" className="alert-btn secondary">
                                    {t('dashboardPage.tryDemo')} <FaGift />
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Quick Levels Browsing Section */}
                    <div className="section-header">
                        <h2>{t('dashboardPage.levels')}</h2>
                        <p>{t('dashboardPage.levelsDescription')}</p>
                    </div>

                    {renderLevelCards()}

                    {/* Subscription Alert */}
                    {isDemoSubscription && (
                        <div className="demo-alert-compact">
                            <div className="demo-alert-grid">
                                <div className="demo-info">
                                    <span className="demo-badge"><FaGift /> {t('dashboardPage.demoLabel')}</span>
                                    <h4>{t('dashboardPage.upgradeGet2Free')}</h4>
                                </div>

                                <div className="demo-timer">
                                    <span className="timer-label"><FaClock /> {t('dashboardPage.expiresIn')}</span>
                                    <div className="timer-values">
                                        <span>{timeRemaining.days}d</span>
                                        <span>{timeRemaining.hours}h</span>
                                        <span>{timeRemaining.minutes}m</span>
                                        <span>{timeRemaining.seconds}s</span>
                                    </div>
                                </div>

                                <div className="demo-action">
                                    <Link to="/subscribe" className="upgrade-btn">
                                        <FaCrown /> {t('dashboardPage.upgrade')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    {isPaidSubscription && daysRemaining !== null && daysRemaining <= 60 && (
                        <div className="alert-card warning-alert">
                            <div className="alert-header">
                                <div className="alert-icon">
                                    <FaExclamationTriangle />
                                </div>
                                <div className="alert-title">
                                    <h3>{t('dashboardPage.subscriptionExpiringSoon')}</h3>
                                    <p>{t('dashboardPage.renewMessage')}</p>
                                </div>
                            </div>
                            <div className="alert-details">
                                <div className="detail-item">
                                    <span className="detail-label">{t('dashboardPage.expiresOn')}</span>
                                    <span className="detail-value">{formatDate(subscription.end_date)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">{t('dashboardPage.daysRemaining')}</span>
                                    <span className="detail-value">{daysRemaining} {t('dashboardPage.days')}</span>
                                </div>
                            </div>
                            <Link to="/renew" className="alert-btn">
                                {t('dashboardPage.renewSubscription')} <FaArrowRight />
                            </Link>
                        </div>
                    )}

                    {/* Quick Actions */}
                    <div className="section-header">
                        <h2>{t('dashboardPage.quickActions')}</h2>
                        <p>{t('dashboardPage.quickActionsDesc')}</p>
                    </div>

                    <div className="actions-grid">
                        <Link to="/profile" className="action-card">
                            <div className="action-icon purple">
                                <FaUser />
                            </div>
                            <h3>{t('dashboardPage.editProfile')}</h3>
                            <p>{t('dashboardPage.updatePersonalInfo')}</p>
                            <span className="action-arrow"><FaArrowRight /></span>
                        </Link>

                        <Link to="/subscription" className="action-card">
                            <div className="action-icon pink">
                                <FaCreditCard />
                            </div>
                            <h3>{t('dashboardPage.manageSubscription')}</h3>
                            <p>{t('dashboardPage.viewUpdatePlan')}</p>
                            <span className="action-arrow"><FaArrowRight /></span>
                        </Link>

                        <Link to="/progress" className="action-card">
                            <div className="action-icon blue">
                                <FaChartBar />
                            </div>
                            <h3>{t('dashboardPage.viewProgress')}</h3>
                            <p>{t('dashboardPage.trackLearning')}</p>
                            <span className="action-arrow"><FaArrowRight /></span>
                        </Link>

                        <div className="action-card disabled">
                            <div className="action-icon green">
                                <FaClipboard />
                            </div>
                            <h3>{t('dashboardPage.learningNotes')}</h3>
                            <p>{t('dashboardPage.personalNotes')}</p>
                            <span className="action-arrow"><FaArrowRight /></span>
                            <span className="coming-soon-tag">{t('dashboardPage.soon')}</span>
                        </div>
                    </div>

                    {/* Resources Section */}
                    <div className="section-header">
                        <h2>{t('dashboardPage.resources')}</h2>
                        <p>{t('dashboardPage.resourcesDesc')}</p>
                    </div>

                    <div className="resources-grid">
                        <div className="resource-card">
                            <div className="resource-icon">
                                <FaQuestionCircle />
                            </div>
                            <h3>{t('dashboardPage.helpCenter')}</h3>
                            <ul>
                                <li><FaCheck /> {t('dashboardPage.faqs')}</li>
                                <li><FaCheck /> {t('dashboardPage.tutorials')}</li>
                                <li><FaCheck /> {t('dashboardPage.contactSupport')}</li>
                                <li><FaCheck /> {t('dashboardPage.userGuides')}</li>
                            </ul>
                        </div>

                        <div className="resource-card">
                            <div className="resource-icon">
                                <FaStar />
                            </div>
                            <h3>{t('dashboardPage.platformFeatures')}</h3>
                            <ul>
                                <li><FaCheck /> {t('dashboardPage.interactiveLessons')}</li>
                                <li><FaCheck /> {t('dashboardPage.progressTracking')}</li>
                                <li><FaCheck /> {t('dashboardPage.personalizedLearning')}</li>
                                <li><FaCheck /> {t('dashboardPage.expertSupport')}</li>
                            </ul>
                        </div>

                        {isDemoSubscription && (
                            <div className="resource-card highlight">
                                <div className="resource-icon">
                                    <FaGift />
                                </div>
                                <h3>{t('dashboardPage.demoBenefits')}</h3>
                                <ul>
                                    <li><FaCheck /> {t('dashboardPage.accessFirst2Lessons')}</li>
                                    <li><FaCheck /> {t('dashboardPage.tryAllFeatures')}</li>
                                    <li><FaCheck /> {t('dashboardPage.noCreditCard')}</li>
                                    <li><FaCheck /> {t('dashboardPage.upgradeAnytime')}</li>
                                </ul>
                                <Link to="/subscription" className="resource-btn">
                                    {t('dashboardPage.upgradeNow')}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
            <Scrollbar />
        </div>
    );
};

export default DashboardPage;